import { useEffect } from "react";
import { customEvaluations } from "../../evaluations";
import {
  dispatchUpdateField,
  getUpdatedStoreValueFromCompanyId,
} from "../../helpers";
import { useUserContext } from "../../../../contexts/UserContext";
import { RenderInputProps } from "../../../../types";
import { useIntl } from "react-intl";
import {
  DOLLAR_PREFIX_FOR_FIELDS,
  PERCENTAGE_SUFFIX_FIELDS,
} from "../../constants";
import { InputWithTooltip } from "../../../common/Tooltip";
import { uniqueId } from "lodash";
import { StyledInlineInput } from "../../../../utils/styled";

export default function RenderInput({
  title,
  propertyKey,
  description,
  rowIndex,
  propertySubKey,
  type,
  isDisabled,
  inputValue,
  isRequired,
  evalMethod,
  storeKey,
  error,
  message,
  maximum,
  minimum,
  isDialogField,
  selectedTabSectionKey,
  dispatch,
  rowDataReducerState,
  isInline = false,
}: Readonly<RenderInputProps>): JSX.Element {
  const { iamUserState } = useUserContext();
  const intl = useIntl();
  const formattedInputType =
    type === "number" || type === "integer" ? "number" : "text";
  const id =
    rowIndex !== undefined && propertySubKey
      ? `-${rowIndex}-${propertySubKey}`
      : "";
  const formattedId = isInline
    ? `${propertyKey}${id}-inline-input`
    : `${propertyKey}${id}-input`;
  const InputComponent = isInline ? StyledInlineInput : InputWithTooltip;
  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue: string | number = event.target.value;

    if (formattedInputType === "number") {
      inputValue = inputValue === "" ? "" : Number(inputValue);
    }

    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue: inputValue,
        rowIndex,
        propertySubKey,
        isDialogField,
        selectedTabSectionKey,
      },
    });
  };

  useEffect(() => {
    if (evalMethod) {
      customEvaluations({
        evalMethod,
        propertyKey,
        propertySubKey,
        rowIndex,
        inputValue,
        searchResponse: undefined,
        rowDataReducerState,
        dispatch,
      });
    }
  }, [inputValue]);

  useEffect(() => {
    // below will update the store field value based on company value
    if (storeKey && inputValue !== "") {
      const storeValueToUpdate = getUpdatedStoreValueFromCompanyId({
        storeKey,
        inputValue,
        iamUserState,
        rowDataReducerState,
      });

      dispatchUpdateField({
        propertyKey,
        propertySubKey,
        rowIndex,
        newFieldValue: storeValueToUpdate,
        isDialogField,
        selectedTabSectionKey,
        targetFieldKey: storeKey,
        dispatch,
      });

      dispatchUpdateField({
        propertyKey,
        propertySubKey,
        rowIndex,
        newFieldValue: storeValueToUpdate,
        isDialogField,
        selectedTabSectionKey,
        targetFieldKey: storeKey,
        isForResetField: true,
        dispatch,
      });
    }
  }, [storeKey]);

  const conditionalProps = isInline
    ? { hideLabel: true }
    : {
        tooltip: description,
        suffixText: PERCENTAGE_SUFFIX_FIELDS.some((term) =>
          title.toLowerCase().includes(term.toLowerCase()),
        )
          ? "%"
          : "",
      };

  return (
    <InputComponent
      dataTestId={formattedId}
      id={uniqueId(formattedId)}
      label={title}
      name={propertyKey}
      value={(inputValue as string | number) ?? ""}
      placeholder={
        isDisabled
          ? title
          : intl.formatMessage(
              { id: "placeholder.enter", defaultMessage: "Enter {item}" },
              { item: title },
            )
      }
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        handleInput(event)
      }
      isDisabled={isDisabled}
      isRequired={isRequired && !isDisabled}
      hasError={error}
      errorMessage={message}
      max={maximum}
      min={minimum}
      enableCustomValidation={true}
      prefixText={
        DOLLAR_PREFIX_FOR_FIELDS.some((term) =>
          title.toLowerCase().includes(term.toLowerCase()),
        )
          ? "$"
          : ""
      }
      type={formattedInputType}
      {...conditionalProps}
    />
  );
}
